import React, { useState, useEffect } from "react";
import "./CallLists.css";
import * as ApiService from "../services/ApiService";
import { useAuth0 } from "@auth0/auth0-react";

const CallLists = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [callLists, setCallLists] = useState([]);
  const [editingCallList, setEditingCallList] = useState(null);
  const [newCallList, setNewCallList] = useState({
    name: "",
    type: "mainline",
    active: true,
  });
  const [error, setError] = useState("");

  // Add tooltip text constants
  const mainlineTooltipText = `
    Mainline call lists represent the primary on-call rotations.<br>
    Each doctor can only be assigned to one mainline call list per day.
  `;

  const adjunctTooltipText = `
    Adjunct call lists serve as supplementary on-call rotations.<br>
    A doctor may be assigned to both an adjunct call list and a mainline call list on the same day.
  `;

  const isDevelopment = process.env.NODE_ENV === "development";

  useEffect(() => {
    fetchCallLists();
  }, []);

  const fetchCallLists = async () => {
    try {
      const data = await ApiService.fetchCallLists(getAccessTokenSilently);
      setCallLists(data);
    } catch (error) {
      console.error("Error fetching call lists:", error);
    }
  };

  const handleAddCallList = async (e) => {
    e.preventDefault();
    try {
      const response = await ApiService.addCallList(
        getAccessTokenSilently,
        newCallList
      );
      if (response.ok) {
        setNewCallList({ name: "", type: "mainline", active: true });
        fetchCallLists();
      }
    } catch (error) {
      console.error("Error adding call list:", error);
    }
  };

  const handleUpdateCallList = async (e) => {
    e.preventDefault();
    try {
      const response = await ApiService.updateCallList(
        getAccessTokenSilently,
        editingCallList.callListTypeId,
        editingCallList
      );
      if (response.ok) {
        setEditingCallList(null);
        fetchCallLists();
      }
    } catch (error) {
      console.error("Error updating call list:", error);
    }
  };

  const handleDeleteCallList = async (callListTypeId) => {
    if (window.confirm("Are you sure you want to delete this call list?")) {
      try {
        const response = await ApiService.deleteCallList(
          getAccessTokenSilently,
          callListTypeId
        );
        if (response.ok) {
          fetchCallLists();
        }
      } catch (error) {
        console.error("Error deleting call list:", error);
      }
    }
  };

  const handleToggleActive = async (callList) => {
    try {
      const updatedCallList = { ...callList, active: !callList.active };
      const response = await ApiService.updateCallList(
        getAccessTokenSilently,
        callList.callListTypeId,
        updatedCallList
      );
      if (response.ok) {
        fetchCallLists();
      }
    } catch (error) {
      console.error("Error toggling call list active state:", error);
    }
  };

  const renderCallListTable = (type) => {
    const filteredLists = callLists.filter((list) => list.type === type);
    const tooltipText =
      type === "mainline" ? mainlineTooltipText : adjunctTooltipText;

    return (
      <div className="call-lists-section">
        <h3
          onMouseEnter={(e) => {
            const tooltip = document.createElement("div");
            tooltip.className = "weight-tooltip";
            tooltip.innerHTML = tooltipText;
            document.body.appendChild(tooltip);
            const rect = e.target.getBoundingClientRect();
            const scrollTop =
              window.pageYOffset || document.documentElement.scrollTop;
            const scrollLeft =
              window.pageXOffset || document.documentElement.scrollLeft;
            tooltip.style.left = `${rect.left + scrollLeft}px`;
            tooltip.style.top = `${rect.bottom + scrollTop + 5}px`;
          }}
          onMouseLeave={() => {
            const tooltip = document.querySelector(".weight-tooltip");
            if (tooltip) tooltip.remove();
          }}
        >
          {type === "mainline" ? "Mainline Call Lists" : "Adjunct Call Lists"}
        </h3>
        <div className="table-container">
          {filteredLists.length === 0 ? (
            <div className="table-no-data">
              No {type === "mainline" ? "mainline" : "adjunct"} call lists have
              been created yet. Create one using the form above.
            </div>
          ) : (
            <table className="call-lists-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLists.map((callList) => (
                  <tr key={callList.callListTypeId}>
                    {editingCallList?.callListTypeId ===
                    callList.callListTypeId ? (
                      <>
                        <td>
                          <input
                            type="text"
                            value={editingCallList.name}
                            onChange={(e) =>
                              setEditingCallList({
                                ...editingCallList,
                                name: e.target.value,
                              })
                            }
                          />
                        </td>
                        <td>
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={editingCallList.active}
                              onChange={(e) =>
                                setEditingCallList({
                                  ...editingCallList,
                                  active: e.target.checked,
                                })
                              }
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button onClick={handleUpdateCallList}>Save</button>
                            <button onClick={() => setEditingCallList(null)}>
                              Cancel
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{callList.name}</td>
                        <td>
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={callList.active}
                              onChange={() => handleToggleActive(callList)}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              onClick={() => setEditingCallList(callList)}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() =>
                                handleDeleteCallList(callList.callListTypeId)
                              }
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="page-container">
      <div className="staff-header">
        <h2>Call List Management</h2>
      </div>

      {/* Add New Call List Form */}
      <form onSubmit={handleAddCallList} className="doctor-form add-call-list">
        <h3>Add New Call List</h3>
        <div className="form-group">
          <input
            type="text"
            placeholder="Call List Name"
            value={newCallList.name}
            onChange={(e) =>
              setNewCallList({ ...newCallList, name: e.target.value })
            }
            required
          />
          <select
            value={newCallList.type}
            onChange={(e) =>
              setNewCallList({ ...newCallList, type: e.target.value })
            }
          >
            <option value="mainline">Mainline</option>
            <option value="adjunct">Adjunct</option>
          </select>
          <button type="submit">Add Call List</button>
        </div>
      </form>

      {/* Call Lists Tables */}
      <div className="call-lists-container">
        {renderCallListTable("mainline")}
        {renderCallListTable("adjunct")}
      </div>
    </div>
  );
};

export default CallLists;
